// services/api.js
import axios from 'axios';

// Token y refresh_token en localStorage (no sessionStorage): tienen que
// sobrevivir a cerrar y volver a abrir la app — clave para la versión de
// escritorio (Electron), donde un operario puede abrir AquaReport ya sin
// conexión y necesita poder seguir con la sesión que dejó abierta, no
// encontrarse la pantalla de login porque sessionStorage se vació al cerrar
// la ventana. Ver AquaApp.js para el arranque "offline-aware" que usa esto.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Interceptor para agregar el token a las solicitudes
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación con refresh token automático
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Si el error es 401 y no es un reintento ya en curso
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Si la petición es el inicio de sesión (/token/) o el propio refresh
      // (/token/refresh/), no reintentar ni encolar: si se deja pasar, un
      // refresh token expirado hace que esta misma petición de refresh vuelva
      // a entrar aquí, se encole esperando el resultado de sí misma (isRefreshing
      // ya está en true) y el formulario quede colgado en "Guardando..." para
      // siempre, sin error visible.
      if (originalRequest.url && (
        originalRequest.url === '/token/' || originalRequest.url === 'token/' ||
        originalRequest.url.endsWith('/token/') || originalRequest.url.endsWith('/token') ||
        originalRequest.url.endsWith('/token/refresh/') || originalRequest.url.endsWith('/token/refresh')
      )) {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem('refresh_token');

      // Si no hay refresh token, cerrar sesión directamente
      if (!refreshToken) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Si ya hay un refresh en curso, encolar esta petición
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await api.post('/token/refresh/', { refresh: refreshToken });
        const newAccessToken = response.data.access;
        localStorage.setItem('token', newAccessToken);
        // ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION (settings.py):
        // cada refresh invalida el refresh_token usado y el servidor manda
        // uno nuevo en la respuesta. Si no se guarda acá, la próxima
        // renovación (~15 min después) manda el token viejo, ya en lista
        // negra, y el servidor responde 401 — cerrando la sesión aunque al
        // refresh token le quedaran días de vigencia.
        if (response.data.refresh) {
          localStorage.setItem('refresh_token', response.data.refresh);
        }
        api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const registroDiarioAPI = {
  getAll: (params = {}) => api.get('/registros/', { params }),
  getById: (id) => api.get(`/registros/${id}/`),
  create: (data) => api.post('/registros/', data),
  update: (id, data) => api.put(`/registros/${id}/`, data),
  delete: (id) => api.delete(`/registros/${id}/`),
  getToday: () => api.get('/registros/hoy/'),
  getLast: (params = { ordering: '-fecha,-id', limit: 1 }) => api.get('/registros/', { params }),
  // Carga masiva: crea muchos registros de una sola llamada, sin Telegram
  // por fila (el backend manda un único resumen al terminar, ver
  // CargaMasivaRegistrosView) y sin recalcular el caudal registro por
  // registro (sería O(n²) con cientos de filas).
  cargaMasiva: (registros) => api.post('/registros/carga-masiva/', { registros }),
};

export const aguaCrudaAPI = {
  getAll: () => api.get('/agua-cruda/'),
  getById: (id) => api.get(`/agua-cruda/${id}/`),
  create: (data) => api.post('/agua-cruda/', data),
  update: (id, data) => api.put(`/agua-cruda/${id}/`, data),
  delete: (id) => api.delete(`/agua-cruda/${id}/`),
};

export const aguaTratadaAPI = {
  getAll: () => api.get('/agua-tratada/'),
  getById: (id) => api.get(`/agua-tratada/${id}/`),
  create: (data) => api.post('/agua-tratada/', data),
  update: (id, data) => api.put(`/agua-tratada/${id}/`, data),
  delete: (id) => api.delete(`/agua-tratada/${id}/`),
};

export const reporteMensualAPI = {
  getByMonth: (year, month) => api.get(`/reporte_mensual/?year=${year}&month=${month}`),
  getAll: () => api.get('/reporte_mensual/'),
  create: (data) => api.post('/reporte_mensual/', data),
};

export const personalAPI = {
  getAll: () => api.get('/personal/'),
  getById: (id) => api.get(`/personal/${id}/`),
  create: (data) => api.post('/personal/', data),
  update: (id, data) => api.put(`/personal/${id}/`, data),
  delete: (id) => api.delete(`/personal/${id}/`),
};

export const grupoAPI = {
  getAll: () => api.get('/grupo/'),
  getById: (id) => api.get(`/grupo/${id}/`),
  create: (data) => api.post('/grupo/', data),
  update: (id, data) => api.put(`/grupo/${id}/`, data),
  delete: (id) => api.delete(`/grupo/${id}/`),
};

export const usuarioAPI = {
  getAll: () => api.get('/users/'),
  getById: (id) => api.get(`/users/${id}/`),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.put(`/users/${id}/`, data),
  delete: (id) => api.delete(`/users/${id}/`),
};

export const logAPI = {
  getAll: () => api.get('/logs/'),
  create: (data) => api.post('/logs/', data),
};

export const alertaAPI = {
  getAll: () => api.get('/alertas/'),
  create: (data) => api.post('/alertas/', data),
  update: (id, data) => api.patch(`/alertas/${id}/`, data),
  marcarLeida: (id) => api.post(`/alertas/${id}/marcar_leida/`),
  // Oculta (solo para el usuario actual) las alertas que ya marcó como
  // leídas — no las borra para los demás perfiles.
  limpiar: () => api.post('/alertas/limpiar/'),
  delete: (id) => api.delete(`/alertas/${id}/`),
};

export const bitacoraAPI = {
  getAll: () => api.get('/bitacoras/'),
  create: (data) => api.post('/bitacoras/', data),
  update: (id, data) => api.put(`/bitacoras/${id}/`, data),
  delete: (id) => api.delete(`/bitacoras/${id}/`),
};

export const ensayoJarrasAPI = {
  getAll: () => api.get('/ensayos/'),
  getById: (id) => api.get(`/ensayos/${id}/`),
  sugerirDosis: (params) => api.get('/ensayos/sugerir-dosis/', { params }),
  create: (data) => api.post('/ensayos/', data),
  update: (id, data) => api.put(`/ensayos/${id}/`, data),
  delete: (id) => api.delete(`/ensayos/${id}/`),
};

export const lavadoFiltroAPI = {
  getAll: (params = {}) => api.get('/lavados-filtro/', { params }),
  create: (data) => api.post('/lavados-filtro/', data),
  update: (id, data) => api.put(`/lavados-filtro/${id}/`, data),
  delete: (id) => api.delete(`/lavados-filtro/${id}/`),
};

export const insumoAPI = {
  getAll: (params = {}) => api.get('/insumos/', { params }),
  create: (data) => api.post('/insumos/', data),
  update: (id, data) => api.put(`/insumos/${id}/`, data),
  resumen: (params = {}) => api.get('/insumos/resumen/', { params }),
};

export const movimientoInsumoAPI = {
  getAll: (params = {}) => api.get('/movimientos-insumo/', { params }),
  create: (data) => api.post('/movimientos-insumo/', data),
  registrarPedido: (data) => api.post('/movimientos-insumo/registrar-pedido/', data),
  delete: (id) => api.delete(`/movimientos-insumo/${id}/`),
};

export const jarraDetalleAPI = {
  create: (data) => api.post('/jarras-detalle/', data),
  update: (id, data) => api.put(`/jarras-detalle/${id}/`, data),
};

export const profileAPI = {
  getProfile: () => api.get('/users/perfil/'),
};

export const dispositivoAPI = {
  getAll: () => api.get('/dispositivos/'),
  update: (id, data) => api.patch(`/dispositivos/${id}/`, data),
  delete: (id) => api.delete(`/dispositivos/${id}/`),
};

export const ipBloqueadaAPI = {
  getAll: () => api.get('/ips-bloqueadas/'),
  create: (data) => api.post('/ips-bloqueadas/', data),
  delete: (id) => api.delete(`/ips-bloqueadas/${id}/`),
};

export const reglaAlertaAPI = {
  getAll: () => api.get('/reglas-alertas/'),
  getById: (id) => api.get(`/reglas-alertas/${id}/`),
  create: (data) => api.post('/reglas-alertas/', data),
  update: (id, data) => api.put(`/reglas-alertas/${id}/`, data),
  delete: (id) => api.delete(`/reglas-alertas/${id}/`),
};

export const normativaAPI = {
  get: () => api.get('/normativa/'),
};

export const reportesAPI = {
  getKPIs: (desde, hasta) => api.get('/reportes/kpis/', { params: { desde, hasta } }),
};

export const informeGerencialAPI = {
  getAll: (params = {}) => api.get('/informes-gerenciales/', { params }),
  create: (data) => api.post('/informes-gerenciales/', data),
  aprobar: (id) => api.post(`/informes-gerenciales/${id}/aprobar/`),
  rechazar: (id, motivo) => api.post(`/informes-gerenciales/${id}/rechazar/`, { motivo }),
  // PDF real (reportlab) del informe: si ya está registrado usa el snapshot
  // inmutable (informeId); si aún es borrador, recalcula desde/hasta.
  descargarPDF: (informeId) => api.get(`/informes-gerenciales/${informeId}/pdf/`, { responseType: 'blob' }),
  descargarPDFBorrador: (desde, hasta) => api.get('/reportes/kpis/pdf/', { params: { desde, hasta }, responseType: 'blob' }),
};

export const registrosMesAPI = {
  getMes: (anio, mes, page = 1, pageSize = 20) =>
    api.get('/registros/mes/', { params: { anio, mes, page, page_size: pageSize } }),
  descargarExcel: (anio, mes) =>
    api.get('/registros/mes/excel/', { params: { anio, mes }, responseType: 'blob' }),
  eliminarMes: (anio, mes) =>
    api.delete('/registros/mes/', { params: { anio, mes } }),
};

export const climaAPI = {
  get: (horas = 6) => api.get('/clima/', { params: { horas } }),
};

export const prediccionAPI = {
  get: (parametro, horas = 6) => api.get('/prediccion/', { params: { parametro, horas } }),
};

export default api;